import { Button, Card, Form, Input, InputNumber, Space, Table, Typography, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import { CompanyModel } from '../../../types/admin';
import { CompanyUser, companyAdminCreateUser, companyAdminListUsers, companyAdminSetUserLevel } from '../../../api/companyAdmin';

interface CompanyMembersTabProps {
    company: CompanyModel;
    canManage: boolean;
}

const CompanyMembersTab: React.FC<CompanyMembersTabProps> = ({
    company,
    canManage,
}) => {
    const [members, setMembers] = useState<CompanyUser[]>([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [memberLevels, setMemberLevels] = useState<Record<string, number>>({});
    const [createMemberForm] = Form.useForm();

    useEffect(() => {
        if (!canManage) return;
        const loadMembers = async () => {
            setMembersLoading(true);
            try {
                const res = await companyAdminListUsers({ companyId: company.id });
                const list: CompanyUser[] = res?.users ?? [];
                setMembers(list);
                const nextLevels: Record<string, number> = {};
                list.forEach((u) => {
                    nextLevels[u.uid] = u.level;
                });
                setMemberLevels(nextLevels);
            } catch (error: any) {
                notification.error({
                    message: 'Failed to load members',
                    description: error?.message || String(error),
                });
            } finally {
                setMembersLoading(false);
            }
        };
        loadMembers();
    }, [company.id, canManage]);

    const handleCreateMember = async (values: any) => {
        try {
            await companyAdminCreateUser({ ...values, companyId: company.id });
            notification.success({ message: 'User created' });
            createMemberForm.resetFields();
            const res = await companyAdminListUsers({ companyId: company.id });
            const list: CompanyUser[] = res?.users ?? [];
            setMembers(list);
            const nextLevels: Record<string, number> = {};
            list.forEach((u) => {
                nextLevels[u.uid] = u.level;
            });
            setMemberLevels(nextLevels);
        } catch (error: any) {
            notification.error({
                message: 'Create failed',
                description: error?.message || String(error),
            });
        }
    };

    const handleMemberLevelChange = (uid: string, level: number) => {
        setMemberLevels((prev) => ({ ...prev, [uid]: Number(level) }));
    };

    const handleUpdateMember = async (uid: string) => {
        try {
            await companyAdminSetUserLevel({
                companyId: company.id,
                uid,
                level: memberLevels[uid],
            });
            notification.success({ message: 'Access updated' });
        } catch (error: any) {
            notification.error({
                message: 'Update failed',
                description: error?.message || String(error),
            });
        }
    };

    const handleRemoveMember = async (uid: string) => {
        try {
            await companyAdminSetUserLevel({
                companyId: company.id,
                uid,
                level: 0,
            });
            notification.success({ message: 'User removed' });
            setMembers((prev) => prev.filter((u) => u.uid !== uid));
            setMemberLevels((prev) => {
                const next = { ...prev };
                delete next[uid];
                return next;
            });
        } catch (error: any) {
            notification.error({
                message: 'Remove failed',
                description: error?.message || String(error),
            });
        }
    };

    if (!canManage) {
        return (
            <Card>
                <Typography.Text type="secondary">
                    You do not have permission to manage members.
                </Typography.Text>
            </Card>
        );
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card title="Add User">
                <Form
                    form={createMemberForm}
                    layout="vertical"
                    onFinish={handleCreateMember}
                >
                    <Space wrap>
                        <Form.Item name="email" label="Email" rules={[{ required: true }]}>
                            <Input style={{ width: 220 }} />
                        </Form.Item>
                        <Form.Item name="password" label="Password" rules={[{ required: true, min: 8 }]}>
                            <Input.Password style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item name="firstName" label="First Name">
                            <Input style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item name="lastName" label="Last Name">
                            <Input style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item name="level" label="Level" initialValue={50}>
                            <InputNumber min={0} style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                Add
                            </Button>
                        </Form.Item>
                    </Space>
                </Form>
            </Card>
            <Card title="Company Users">
                <Table
                    rowKey="uid"
                    dataSource={members}
                    pagination={false}
                    loading={membersLoading}
                    columns={[
                        { title: 'Name', render: (_, r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || 'N/A' },
                        { title: 'Email', dataIndex: 'email' },
                        { title: 'Level', dataIndex: 'level' },
                        {
                                    title: 'New Level',
                                    render: (_, r) => (
                                        <InputNumber
                                            min={0}
                                            value={memberLevels[r.uid]}
                                            onChange={(val) => handleMemberLevelChange(r.uid, Number(val))}
                                        />
                                    ),
                                },
                                {
                                    title: 'Actions',
                                    render: (_, r) => (
                                        <Space>
                                            <Button onClick={() => handleUpdateMember(r.uid)}>
                                                Update
                                            </Button>
                                            <Button danger onClick={() => handleRemoveMember(r.uid)}>
                                                Remove
                                            </Button>
                                        </Space>
                            ),
                        },
                    ]}
                />
            </Card>
        </Space>
    );
};

export default CompanyMembersTab;
